<div class="mx-auto w-full max-w-4xl px-4 pb-8 pt-4 sm:px-6">
  <!-- Back to the events list, carrying the row highlight + page size the list expects. -->
  <a class="inline-flex items-center gap-x-1.5 text-sm font-semibold text-gray-600 transition-colors duration-200 hover:text-theme-primary-700"
     [routerLink]="['/' + Routes.LocationModule + '/' + LocationRoutes.Events]"
     [queryParams]="backQueryParams" queryParamsHandling="merge">
    <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd"
            d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-4.5-.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5z"/>
    </svg>
    Zurück zur Übersicht
  </a>

  <!-- Header: what this event is, when it runs, and whether employees can see it. -->
  <header class="mt-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
    <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
      <h1 class="text-xl font-bold tracking-tight text-gray-900">{{ event.name }}</h1>
      <span class="inline-flex flex-none items-center rounded-full bg-indigo-50 px-2 py-0.5 align-top text-xs font-semibold text-indigo-700">
        Virtuell
      </span>
    </div>

    <p class="mt-1 text-sm text-gray-500">
      {{ event.start | date: "dd.MM.yyyy HH:mm" }} – {{ event.end | date: "dd.MM.yyyy HH:mm" }} Uhr
    </p>

    <div class="mt-3 flex flex-wrap items-center gap-2">
      @if (event.public) {
        <span class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
          Freigegeben für Mitarbeiter
        </span>
        <span class="text-xs text-gray-500">
          Anmeldung bis {{ event.publicResponseEnd | date: "dd.MM.yyyy HH:mm" }} Uhr
        </span>
      } @else {
        <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
          Nicht freigegeben
        </span>
      }
    </div>

    <p class="mt-3 text-sm text-gray-500">
      Virtuelle Events dienen ausschließlich der Planung. Sie erscheinen im Dienstplan und im
      Mitarbeiterkalender, werden aber von keiner Kasse erfasst — es gibt daher weder Umsätze noch Auswertungen.
    </p>
  </header>

  <!-- Aktionen: one tile per thing that actually applies to a virtual event. Never a Download, never
       Mitarbeiter-Umsätze, never eine Standortkonfiguration — see the component's own doc comment. -->
  <h2 class="mb-2 mt-4 text-base font-bold tracking-tight text-gray-800">Aktionen</h2>
  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
    @if (canOpenShiftMaker) {
      <a class="group flex h-full flex-col gap-y-1 rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:border-gray-200 hover:shadow-md active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500"
         data-testid="tile-shift-maker"
         [routerLink]="['/' + Routes.CompanyModule + '/' + CompanyRoutes.ShiftMaker]"
         [queryParams]="shiftMakerQueryParams">
        <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-theme-primary-50 text-theme-primary-600 transition-transform duration-200 group-hover:scale-110">
          <svg aria-hidden="true" class="h-5 w-5" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zM1 3.857C1 3.384 1.448 3 2 3h12c.552 0 1 .384 1 .857v10.286c0 .473-.448.857-1 .857H2c-.552 0-1-.384-1-.857V3.857z"/>
            <path
              d="M6.5 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-9 3a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-9 3a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
          </svg>
        </span>
        <span class="mt-2 text-base font-bold tracking-tight text-gray-800">Dienstplan</span>
        <span class="text-sm text-gray-500">Mitarbeiter für dieses Event einteilen.</span>
      </a>
    }

    @if (canOpenSettings) {
      <button type="button" (click)="openSettings()"
              data-testid="tile-settings"
              class="group flex h-full flex-col gap-y-1 rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:border-gray-200 hover:shadow-md active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500">
        <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-theme-primary-50 text-theme-primary-600 transition-transform duration-200 group-hover:scale-110">
          <svg aria-hidden="true" class="h-5 w-5" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001zm-.644.766a.5.5 0 0 0-.707 0L1.95 11.756l-.764 3.057 3.057-.764L14.44 3.854a.5.5 0 0 0 0-.708l-1.585-1.585z"/>
          </svg>
        </span>
        <span class="mt-2 text-base font-bold tracking-tight text-gray-800">Einstellungen</span>
        <span class="text-sm text-gray-500">Name, Freigabe und Anmeldeschluss bearbeiten.</span>
      </button>
    }

    @if (!canOpenShiftMaker && !canOpenSettings) {
      <p class="rounded-2xl border-2 border-dashed border-gray-100 py-6 text-center text-sm italic text-gray-500 sm:col-span-2 lg:col-span-3">
        Für dieses Event stehen Ihnen keine Aktionen zur Verfügung.
      </p>
    }
  </div>
</div>
